/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { LogLevel } from '@azure/msal-browser'
/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */

/*
    tenantID is the same for all application
    clientID `c737ad14-5757-436a-9f3f-3254ab7cc184` --> DEV - PORTALE TI - SPA
*/
export const msalConfig = {
  auth: {
    clientId: window._env_.CLIENT_ID || 'c737ad14-5757-436a-9f3f-3254ab7cc184', // This is the ONLY mandatory field that you need to supply.
    authority:
      'https://login.microsoftonline.com/' + (window._env_.TENANT_ID || 'c6ed042e-462e-4fe7-89ec-ad901855590f'), // Defaults to "https://login.microsoftonline.com/common"
    redirectUri: '/', // You must register this URI on Azure Portal/App Registration. Defaults to window.location.origin
    postLogoutRedirectUri: '/', // Indicates the page to navigate after logout.
    navigateToLoginRequestUrl: false, // If "true", will navigate back to the original request location before processing the auth code response.
  },
  cache: {
    cacheLocation: 'sessionStorage', // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
    storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message)
            return
          case LogLevel.Info:
            console.info(message)
            return
          case LogLevel.Verbose:
            console.debug(message)
            return
          case LogLevel.Warning:
            console.warn(message)
            return
          default:
            console.log(message)
            return
        }
      },
    },
  },
}

/**
 * Add here the endpoints and scopes when obtaining an access token for protected web APIs. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
/*
  scope: `api://eb803767-d8ee-493b-bbe0-0dc17a8f697b/access_as_user` --> DEV - PORTALE TI - SPA
 */
export const protectedResources = {
  apiPortal: {
    absenceEndpoint: (window._env_.MIDDLEWARE_ENDPOINT || 'http://localhost:3010/') + 'api/absence',
    clientiCommesseEndpoint: (window._env_.MIDDLEWARE_ENDPOINT || 'http://localhost:3010/') + 'api/getClientiCommesse',
    adminAbsenceEndpoint: (window._env_.MIDDLEWARE_ENDPOINT || 'http://localhost:3010/') + 'api/adminAbsence',
    absencesMonthWindowEndpoint:
      (window._env_.MIDDLEWARE_ENDPOINT || 'http://localhost:3010/') + 'api/absencesMonthWindow',
    templatesEndpoint: (window._env_.MIDDLEWARE_ENDPOINT || 'http://localhost:3010/') + 'api/templates',
    compilationsEndpoint: (window._env_.MIDDLEWARE_ENDPOINT || 'http://localhost:3010/') + 'api/survey',
    reservationEndpoint: (window._env_.MIDDLEWARE_ENDPOINT || 'http://localhost:3010/') + 'api/reservation',
    hrGameEndpoint: (window._env_.MIDDLEWARE_ENDPOINT || 'http://localhost:8080/') + 'api/hrGame',
    scopes: [window._env_.SCOPE || 'api://eb803767-d8ee-493b-bbe0-0dc17a8f697b/access_as_user'],
  },
}

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
  // scopes: [...protectedResources.apiPortal.scopes],
  scopes: [...protectedResources.apiPortal.scopes, 'User.Read.All'],
}

export const appRoles = {
  AdminAbsence: 'ApproveOrRejectAbsence',
  AdminSurvey: 'ManageSurvey',
  AdminReservation: 'ManageReservation',
}

/**
 * Add here the scopes to request when obtaining an access token for MS Graph API. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
export const graphConfig = {
  graphMeEndpoint: 'https://graph.microsoft.com/v1.0/me',
  graphUsersEndpoint: 'https://graph.microsoft.com/v1.0/users',
  scopes: ['User.Read.All'],
}
